import React, { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ScrollText,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
} from 'lucide-react'
import { getAuditLog } from '../../services/api'
import { AuditAction } from '../../types/enums'
import { AUDIT_CONFIG } from '../../constants/config'
import type { AuditEntry } from '../../types'

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: AuditAction.LOGIN, label: 'Login' },
  { value: AuditAction.SEARCH, label: 'Search' },
  { value: AuditAction.VIEW, label: 'View' },
  { value: AuditAction.ADD, label: 'Add' },
  { value: AuditAction.DELETE, label: 'Delete' },
  { value: AuditAction.SHARE, label: 'Share' },
  { value: AuditAction.MODERATE, label: 'Moderate' },
  { value: AuditAction.ADMIN, label: 'Admin' },
]

const PAGE_SIZE = AUDIT_CONFIG.PAGE_SIZE

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function actionBadge(action: string): React.ReactNode {
  const colors: Record<string, string> = {
    [AuditAction.LOGIN]: 'bg-blue-100 text-blue-700',
    [AuditAction.SEARCH]: 'bg-purple-100 text-purple-700',
    [AuditAction.VIEW]: 'bg-gray-100 text-gray-700',
    [AuditAction.ADD]: 'bg-green-100 text-green-700',
    [AuditAction.DELETE]: 'bg-red-100 text-red-700',
    [AuditAction.SHARE]: 'bg-teal-100 text-teal-700',
    [AuditAction.MODERATE]: 'bg-amber-100 text-amber-700',
    [AuditAction.ADMIN]: 'bg-acad-100 text-acad-700',
  }
  const colorClass = colors[action] ?? 'bg-gray-100 text-gray-700'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {action}
    </span>
  )
}

export default function AuditLog() {
  const [userFilter, setUserFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [page, setPage] = useState(1)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Applied filters (only update on "Apply Filters" click)
  const [appliedFilters, setAppliedFilters] = useState<{
    user_id?: string
    action_type?: string
    from?: string
    to?: string
  }>({})

  const { data: entries, isLoading } = useQuery<AuditEntry[]>({
    queryKey: ['admin', 'audit', appliedFilters],
    queryFn: () => getAuditLog(appliedFilters),
    refetchInterval: autoRefresh ? AUDIT_CONFIG.AUTO_REFRESH_INTERVAL_MS : false,
  })

  const allEntries = entries ?? []

  const totalPages = Math.max(1, Math.ceil(allEntries.length / PAGE_SIZE))
  const pagedEntries = allEntries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleApplyFilters = useCallback(() => {
    const filters: Record<string, string> = {}
    if (userFilter) filters.user_id = userFilter
    if (actionFilter) filters.action_type = actionFilter
    if (dateFrom) filters.from = dateFrom
    if (dateTo) filters.to = dateTo
    setAppliedFilters(filters)
    setPage(1)
  }, [userFilter, actionFilter, dateFrom, dateTo])

  const toggleRow = useCallback((eventId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(eventId)) {
        next.delete(eventId)
      } else {
        next.add(eventId)
      }
      return next
    })
  }, [])

  const handleExportCsv = useCallback(() => {
    if (allEntries.length === 0) return

    const headers = ['Timestamp', 'User', 'Action', 'Details', 'IP Address']
    const rows = allEntries.map((e) => [
      e.timestamp,
      e.user_id,
      e.action_type,
      JSON.stringify(e.details),
      e.ip_address ?? '',
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${AUDIT_CONFIG.CSV_FILENAME_PREFIX}${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }, [allEntries])

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-acad-500" />
            Audit Log
          </h1>
          <p className="text-gray-500 mt-1">Monitor user activity and system events</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm
                       font-medium transition-colors ${
                         autoRefresh
                           ? 'border-acad-300 bg-acad-50 text-acad-700'
                           : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                       }`}
          >
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto-refresh
          </button>
          <button
            onClick={handleExportCsv}
            disabled={allEntries.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-acad-500 text-white rounded-lg
                       text-sm font-medium hover:bg-acad-600 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">User Email</label>
            <input
              type="text"
              placeholder="Filter by user email..."
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-acad-500"
            />
          </div>
          <div className="w-full lg:w-48">
            <label className="block text-xs font-medium text-gray-500 mb-1">Action Type</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                         focus:outline-none focus:ring-2 focus:ring-acad-500"
            >
              {ACTION_TYPES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
          <div className="w-full lg:w-44">
            <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-acad-500"
            />
          </div>
          <div className="w-full lg:w-44">
            <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-acad-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleApplyFilters}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-800 text-white rounded-lg
                         text-sm font-medium hover:bg-gray-900 transition-colors whitespace-nowrap"
            >
              <Filter className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="w-8 px-2" />
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Details</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-2 py-3"><div className="w-4 h-4 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-32 animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-40 animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16 animate-pulse" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><div className="h-4 bg-gray-100 rounded w-48 animate-pulse" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><div className="h-4 bg-gray-100 rounded w-28 animate-pulse" /></td>
                  </tr>
                ))
              ) : pagedEntries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No audit entries found</p>
                  </td>
                </tr>
              ) : (
                pagedEntries.map((entry) => {
                  const isExpanded = expandedRows.has(entry.event_id)
                  return (
                    <React.Fragment key={entry.event_id}>
                      <tr
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleRow(entry.event_id)}
                      >
                        <td className="px-2 py-3 text-center">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 mx-auto" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatTimestamp(entry.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{entry.user_id}</td>
                        <td className="px-4 py-3">{actionBadge(entry.action_type)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell max-w-xs truncate">
                          {typeof entry.details === 'object'
                            ? JSON.stringify(entry.details).slice(0, 60)
                            : String(entry.details)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400 hidden md:table-cell">
                          {entry.ip_address ?? '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="px-4 py-3 bg-gray-50">
                            <div className="text-sm">
                              <p className="font-medium text-gray-700 mb-2">Full Details</p>
                              <pre className="bg-white rounded-lg border border-gray-200 p-3 text-xs text-gray-600 overflow-x-auto">
                                {JSON.stringify(entry.details, null, 2)}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {allEntries.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1} to{' '}
              {Math.min(page * PAGE_SIZE, allEntries.length)} of {allEntries.length} entries
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-50
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-50
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
